import { useEffect, useRef } from "react";
import { vectorTileRegistry } from "../../apis/VectorTileRegistry";
import { sharedPopupManager } from "../../utils/popup/SharedPopupManager";
import { reorderVectorTileLayers } from "../../utils/MapLayerUtils";
import { toBackendDepartmentCode } from "../../utils/departmentCode";
import AuthAPI from "../../apis/auth/AuthAPI";
import i18n from "../../i18n/i18n";

const authApi = new AuthAPI();

const useVectorTile = (
  mapRef,
  layerKey,
  opacity = 1,
  show = false,
  options = {},
) => {
  const isVisibleRef = useRef(show);
  const cleanupTimeoutRef = useRef(null);

  const config = vectorTileRegistry[layerKey];
  const isDynamic = config?.isDynamic;

  // Build the query string shared by both tile URLs and identify calls
  const buildQueryParams = (opts) => {
    const params = new URLSearchParams();
    if (opts?.country) params.append("country", opts.country);

    // Handle department as array for multiple selection
    if (
      opts?.department &&
      Array.isArray(opts.department) &&
      opts.department.length > 0
    ) {
      // If 'tous' is selected or all departments are selected, don't filter by department
      const depts = opts.department.filter((d) => d !== "");
      if (depts.length > 0 && depts.length < 95) {
        depts.forEach((dept) =>
          params.append("department", toBackendDepartmentCode(dept)),
        );
      }
    } else if (
      opts?.department &&
      typeof opts.department === "string" &&
      opts.department !== ""
    ) {
      params.append("department", toBackendDepartmentCode(opts.department));
    }
    return params.toString();
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!config) {
      console.error(`No config found for vector layer: ${layerKey}`);
      return;
    }

    isVisibleRef.current = show;

    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    const sourceId = config.sourceId || config.id;
    const layers = config.layers || [config.layer];

    // 🔑 Validate/refresh auth exactly once per "show" transition (or per
    // dynamic-options change), right before this layer starts requesting
    // tiles. This effect only re-runs when `show`/`layerKey`/options change —
    // never on zoom/pan/bbox changes — so this fires once per toggle,
    // not once per tile. Token refresh (via x-access-token header) and
    // 401 logout are both handled automatically by the shared axios
    // interceptor that AuthAPI's underlying `api` instance uses.
    if (show) {
      authApi.checkAuth().catch(() => {
        // 401 handling already lives in the shared axios interceptor
      });
    }

    const applyLayer = () => {
      if (!show) return;

      // Init popup manager
      if (options.enablePopup) {
        sharedPopupManager.init(map);
      }

      // 1. Build tile URL with country/department params if dynamic
      let tilesUrl = config.source.tiles?.[0];
      if (isDynamic && tilesUrl) {
        const queryString = buildQueryParams(options);
        if (queryString) {
          tilesUrl = `${tilesUrl}?${queryString}`;
        }
      }

      // 2. Add or update source
      const existingSource = map.getSource(sourceId);
      if (!existingSource) {
        const sourceConfig = tilesUrl
          ? { ...config.source, tiles: [tilesUrl] }
          : config.source;
        map.addSource(sourceId, sourceConfig);
      } else if (isDynamic && tilesUrl) {
        existingSource.setTiles([tilesUrl]);
      }

      // 3. Load symbol image if needed
      if (config.symbol && !map.hasImage(config.symbol.iconName)) {
        map.loadImage(config.symbol.iconUrl, (error, image) => {
          if (error) return;
          if (!map.hasImage(config.symbol.iconName)) {
            map.addImage(config.symbol.iconName, image);
          }
        });
      }

      // 4. Add or show layers
      layers.forEach((layerConfig) => {
        const layerId = layerConfig.id;
        if (!map.getLayer(layerId)) {
          const anchorLayer = options.priorityLayers?.find((id) =>
            map.getLayer(id),
          );

          const fullLayerConfig = {
            ...layerConfig,
            source: sourceId,
            "source-layer":
              layerConfig["source-layer"] || layerConfig.sourceLayer,
            layout: { ...layerConfig.layout, visibility: "visible" },
          };
          if (fullLayerConfig.sourceLayer) delete fullLayerConfig.sourceLayer;

          if (layerConfig.type === "line") {
            fullLayerConfig.paint = {
              ...layerConfig.paint,
              "line-opacity": opacity,
            };
          } else if (layerConfig.type === "fill") {
            fullLayerConfig.paint = {
              ...layerConfig.paint,
              "fill-opacity": opacity,
            };
          } else if (layerConfig.type === "symbol") {
            fullLayerConfig.paint = {
              ...layerConfig.paint,
              "icon-opacity": opacity,
            };
          }

          map.addLayer(fullLayerConfig, anchorLayer || undefined);
        } else {
          map.setLayoutProperty(layerId, "visibility", "visible");
          if (layerConfig.type === "line")
            map.setPaintProperty(layerId, "line-opacity", opacity);
          else if (layerConfig.type === "fill")
            map.setPaintProperty(layerId, "fill-opacity", opacity);
          else if (layerConfig.type === "symbol")
            map.setPaintProperty(layerId, "icon-opacity", opacity);
        }
      });

      // 5. Layer ordering
      if (options.priorityLayers) {
        options.priorityLayers.forEach((priorityLayerId) => {
          if (map.getLayer(priorityLayerId)) {
            reorderVectorTileLayers(
              map,
              layers.map((l) => l.id),
              priorityLayerId,
            );
          }
        });
      }

      // 6. Register with popup manager
      if (options.enablePopup && config.identifyUrl) {
        const fetchFn = async (lng, lat) => {
          try {
            let url = `${config.identifyUrl}?lat=${lat}&lng=${lng}`;
            const queryString = buildQueryParams(options);
            if (isDynamic && queryString) {
              url += `&${queryString}`;
            }
            const response = await fetch(url);
            if (!response.ok) return null;
            const data = await response.json();
            const name = data.nom || null;
            const code = data.code || null;

            if (!name) return null;
            return {
              label: i18n.t(config.labelKey || `layers.items.${layerKey}`, {
                defaultValue: config.label || config.name || layerKey,
              }),
              code,
              name,
            };
          } catch {
            return null;
          }
        };

        sharedPopupManager.register(layerKey, fetchFn);
      }
    };

    if (show) {
      applyLayer();
    } else {
      layers.forEach((layerConfig) => {
        if (map.getLayer(layerConfig.id)) {
          map.setLayoutProperty(layerConfig.id, "visibility", "none");
        }
      });

      if (options.enablePopup) {
        sharedPopupManager.unregister(layerKey);
      }

      cleanupTimeoutRef.current = setTimeout(() => {
        if (!isVisibleRef.current) {
          layers.forEach((layerConfig) => {
            if (map.getLayer(layerConfig.id)) map.removeLayer(layerConfig.id);
          });
          if (map.getSource(sourceId)) map.removeSource(sourceId);
          if (config.symbol && map.hasImage(config.symbol.iconName)) {
            map.removeImage(config.symbol.iconName);
          }
        }
      }, 5000);
    }

    map.on("style.load", applyLayer);

    return () => {
      if (cleanupTimeoutRef.current) clearTimeout(cleanupTimeoutRef.current);
      if (options.enablePopup) sharedPopupManager.unregister(layerKey);
      map.off("style.load", applyLayer);
    };
  }, [
    mapRef,
    layerKey,
    show,
    options.priorityLayers,
    options.enablePopup,
    ...(isDynamic ? [options.country, options.department] : []),
  ]); // ← re-run when country OR department changes

  return null;
};

export default useVectorTile;
